import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useWishlist } from '../contexts/WishlistContext';
import { useCart } from '../contexts/CartContext';
import { useCurrency } from '../contexts/CurrencyContext';
import toast from 'react-hot-toast';
import {
  TrashIcon,
  ShoppingBagIcon,
  ArrowLeftIcon,
  HeartIcon
} from '@heroicons/react/24/outline';

const Wishlist = () => {
  const { wishlistItems, loading, removeFromWishlist } = useWishlist();
  const { addToCart } = useCart();
  const { formatPrice } = useCurrency();
  const [busyItems, setBusyItems] = useState({});

  const setBusy = (productId, value) => {
    setBusyItems((prev) => ({ ...prev, [productId]: value }));
  };

  const handleRemove = async (productId) => {
    setBusy(productId, true);
    const result = await removeFromWishlist(productId);
    setBusy(productId, false);
    if (result.success) {
      toast.success('Removed from wishlist');
    } else {
      toast.error(result.message || 'Failed to remove item');
    }
  };

  const handleMoveToCart = async (item) => {
    setBusy(item.product_id, true);
    const cartResult = await addToCart(item.product_id, 1);
    if (cartResult.success) {
      await removeFromWishlist(item.product_id);
      toast.success('Moved to cart');
    } else {
      toast.error(cartResult.message || 'Failed to add to cart');
    }
    setBusy(item.product_id, false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="relative inline-block">
            <div className="w-16 h-16 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin"></div>
          </div>
          <p className="mt-4 text-gray-600">Loading your wishlist...</p>
        </div>
      </div>
    );
  }

  if (wishlistItems.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 flex items-center justify-center">
        <div className="text-center bg-white p-12 rounded-2xl shadow-xl max-w-md">
          <HeartIcon className="w-24 h-24 text-gray-300 mx-auto mb-6" />
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Your Wishlist is Empty</h1>
          <p className="text-gray-600 mb-8">Save items you love here to buy them later.</p>
          <Link
            to="/products"
            className="inline-block bg-gradient-to-r from-indigo-500 to-purple-500 text-white px-8 py-4 rounded-xl hover:from-indigo-600 hover:to-purple-600 transition-all duration-300 transform hover:scale-105 shadow-lg font-semibold"
          >
            Continue Shopping
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-bold text-gray-900 mb-2">My Wishlist</h1>
            <p className="text-gray-600">{wishlistItems.length} {wishlistItems.length === 1 ? 'item' : 'items'}</p>
          </div>
          <Link
            to="/products"
            className="flex items-center gap-2 text-indigo-600 hover:text-indigo-700 font-semibold"
          >
            <ArrowLeftIcon className="w-5 h-5" />
            Continue Shopping
          </Link>
        </div>

        <div className="space-y-4">
          {wishlistItems.map((item) => (
            <div key={item.id} className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
              <div className="p-6">
                <div className="flex flex-col sm:flex-row gap-6">
                  <Link to={`/products/${item.product_id}`} className="flex-shrink-0">
                    <img
                      src={item.image_url || '/api/placeholder/150/150'}
                      alt={item.name || 'Product'}
                      className="w-32 h-32 object-cover rounded-xl hover:scale-105 transition-transform duration-300"
                    />
                  </Link>

                  <div className="flex-1">
                    <Link to={`/products/${item.product_id}`}>
                      <h3 className="text-xl font-bold text-gray-900 mb-2 hover:text-indigo-600 transition-colors">
                        {item.name || 'Product'}
                      </h3>
                    </Link>
                    <p className="text-lg font-semibold text-indigo-600 mb-4">
                      {formatPrice(item.price)}
                    </p>

                    {!item.is_active && (
                      <p className="text-sm text-red-600 font-semibold mb-2">No longer available</p>
                    )}
                    {item.is_active === 1 && item.stock_quantity <= 0 && (
                      <p className="text-sm text-red-600 font-semibold mb-2">Out of stock</p>
                    )}

                    <div className="flex items-center gap-4">
                      <button
                        onClick={() => handleMoveToCart(item)}
                        disabled={busyItems[item.product_id] || !item.is_active || item.stock_quantity <= 0}
                        className="flex items-center gap-2 bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <ShoppingBagIcon className="w-5 h-5" />
                        Move to Cart
                      </button>
                      <button
                        onClick={() => handleRemove(item.product_id)}
                        disabled={busyItems[item.product_id]}
                        className="flex items-center gap-2 text-red-600 hover:text-red-700 font-semibold transition-colors disabled:opacity-50"
                      >
                        <TrashIcon className="w-5 h-5" />
                        Remove
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Wishlist;
